import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger'; 
import { Link } from 'react-router-dom';
import { PROPERTY_IMAGES } from '../constants/images';
import { tursoAPI as propertyAPI } from '../services/tursoApi';


gsap.registerPlugin(ScrollTrigger);

const PropertyCard = ({ property, index }) => {
  const cardRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState(property.images[0]?.url || PROPERTY_IMAGES.placeholder);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  useEffect(() => {
    const img = new Image();
    img.src = property.images[0]?.url;
    img.onload = () => {
      setImageSrc(property.images[0].url);
      setImageLoaded(true);
    };
    img.onerror = handleImageError;

    const ctx = gsap.context(() => {
      gsap.from(cardRef.current, {
        opacity: 0,
        y: 50,
        duration: 0.6, 
        delay: index * 0.1, 
        scrollTrigger: {
          trigger: cardRef.current,
          start: "top bottom-=100",
          toggleActions: "play none none reset" 
        }
      });
    });

    return () => ctx.revert();
  }, [index, property]); 

  const handleImageError = () => {
    if (retryCount < maxRetries) {
      setRetryCount(retryCount + 1);
      setImageSrc(PROPERTY_IMAGES.fallback[property.propertyType] || PROPERTY_IMAGES.placeholder);
    } else {
      setImageSrc(PROPERTY_IMAGES.placeholder);
      setImageLoaded(true);
    }
  };

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(1)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(1)} L`;
    }
    return `₹${price.toLocaleString()}`;
  };

  return (
    <Link to={`/properties/${property.slug}`} className="group block transform transition-all duration-300 hover:-translate-y-1">
      <div
        ref={cardRef}
        className="relative overflow-hidden rounded-3xl bg-white shadow-lg transition-all duration-300 group-hover:shadow-xl"
      >
        <div className="aspect-[4/3] overflow-hidden bg-gray-100">
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          <img
            src={imageSrc}
            alt={property.title}
            className={`object-cover w-full h-full transition-all duration-700 group-hover:scale-110 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={handleImageError}
          />
        </div>
        <div className="p-6 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-text-secondary">
              {property.location.city}, {property.location.state}
            </span>
            <span className="text-lg font-bold text-accent">{formatPrice(property.price)}</span>
          </div>
          <h3 className="text-xl font-display font-bold text-text-primary group-hover:text-accent transition-colors line-clamp-2">
            {property.title}
          </h3>
          <p className="text-text-secondary text-sm font-medium">
            {property.features.bedrooms} Beds • {property.features.bathrooms} Baths • {property.features.area} sq.ft
          </p>
          <div className="pt-4">
            <span className="inline-flex items-center text-sm font-semibold text-accent group-hover:translate-x-1 transition-transform">
              View Details
              <svg className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
          </div>
        </div>
        {property.featured && (
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md">
            <span className="text-sm font-semibold text-accent">Featured</span>
          </div>
        )}
      </div>
    </Link>
  );
};

const FeaturedProperties = () => {
  const sectionRef = useRef(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const data = await propertyAPI.getFeaturedProperties();
        setProperties(data.properties || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching featured properties:', err);
        setError('Failed to load featured properties');
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  useEffect(() => {
    if (sectionRef.current && !loading) {
      const ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom-=100",
            toggleActions: "play none none reset" // Changed from "reverse" to "reset"
          }
        });

        tl.from(sectionRef.current.querySelector('h2'), {
          opacity: 0,
          y: 30,
          duration: 0.6 
        })
        .from(sectionRef.current.querySelector('p'), {
          opacity: 0,
          y: 20,
          duration: 0.5 
        }, "-=0.3"); 
      });

      return () => ctx.revert();
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="py-24 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-24 text-center">
        <p className="text-text-secondary">{error}</p>
      </div>
    );
  }

  if (!properties || properties.length === 0) {
    return (
      <div className="py-24 text-center">
        <p className="text-text-secondary">No featured properties available at the moment.</p>
      </div>
    );
  }

  return (
    <section ref={sectionRef} className="py-24 bg-background-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl sm:text-5xl font-display font-bold text-text-primary mb-6">
            Featured Properties
          </h2>
          <p className="text-text-secondary text-lg sm:text-xl">
            Discover our handpicked selection of premium properties across India
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {properties.slice(0,3).map((property, index) => (
            <PropertyCard key={property.id} property={property} index={index} />
          ))}
        </div>
        <div className="mt-16 text-center">
          <Link
            to="/properties"
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-accent hover:bg-primary-700 transition-all duration-300 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 group"
          >
            View All Properties
            <svg
              className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProperties;